<template>
  <div class="age-gender-chart">
    <!-- 年龄分布环形图 -->
    <div class="age-chart-container">
      <div id="ageChart" class="age-chart"></div>
    </div>
    
    <!-- 性别比例 -->
    <div class="gender-ratio">
      <div 
        class="gender-item" 
        v-for="(item, index) in genderData" 
        :key="index"
      >
        <div class="gender-icon" :class="getGenderClass(item.name)">
          <span class="gender-symbol">{{ getGenderSymbol(item.name) }}</span>
        </div>
        <div class="gender-info">
          <div class="gender-percent">{{ item.percent }}%</div>
          <div class="gender-label">{{ item.value }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'
import resize from "@/components/Charts/mixins/resize";

export default {
  name: 'AgeGenderChart',
  mixins: [resize],
  props: {
    sqName: {
      type: String,
      default: ''
    },
    dbName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      chart: null,
      genderData: [],
      ageData: [],
      // 性别配置映射
      genderConfig: {
        '男': { class: 'male', symbol: '♂', label: '男性占比' },
        'male': { class: 'male', symbol: '♂', label: '男性占比' },
        '女': { class: 'female', symbol: '♀', label: '女性占比' },
        'female': { class: 'female', symbol: '♀', label: '女性占比' }
      }
    }
  },
  mounted() {
    this.loadGenderData()
    this.initAgeChart()
    this.getAgeData()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    // 加载性别数据
    loadGenderData() {
      this.$http.post(process.env.VUE_APP_API_HOST + "/patient_gender_pie", {
        "extra_params": {
          "shengqu_name": this.sqName,
          "diban_name": this.dbName,
          "date_start": "2025-05-04 00:00:00",
          "date_end": "2026-05-04 23:59:59"
        }
      }).then((res) => {
        // eslint-disable-next-line no-debugger
        // debugger
        this.processGenderData(res.data.data || [])
      }).catch(() => {
        // 接口失败时使用示例数据
        this.processGenderData([
          { name: '男', value: 52 },
          { name: '女', value: 48 }
        ])
      })
    },
    // 处理性别数据，计算百分比
    processGenderData(data) {
      const total = data.reduce((sum, item) => sum + (item.value || 0), 0)
      this.genderData = data.map(item => ({
        name: item.name,
        value: item.value,
        percent: total > 0 ? Math.round((item.value / total) * 100) : 0,
        label: this.getGenderLabel(item.name)
      }))
    },
    // 获取性别标签
    getGenderLabel(name) {
      return this.genderConfig[name]?.label || `${name}占比`
    },
    // 获取性别CSS类
    getGenderClass(name) {
      return this.genderConfig[name]?.class || ''
    },
    // 获取性别符号
    getGenderSymbol(name) {
      return this.genderConfig[name]?.symbol || ''
    },
    initAgeChart() {
      this.$nextTick(() => {
        const chartDom = document.getElementById('ageChart')
        if (!chartDom) return
        
        this.chart = echarts.init(chartDom, 'dark')
        
        const option = {
          backgroundColor: 'transparent',
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderColor: '#74ffbe',
            borderWidth: 1,
            textStyle: {
              color: '#fff'
            }
          },
          // 颜色配置：橙色、黄色、紫色、红色、青色、绿色等
          color: ['#ff9a56', '#fac858', '#906bf9', '#ee6666', '#73c0de', '#91cc75', '#ff9ff3', '#feca57'],
          series: [
            {
              name: '年龄分布',
              type: 'pie',
              radius: ['15%', '75%'],
              center: ['50%', '50%'],
              roseType: 'area',
              avoidLabelOverlap: true,
              itemStyle: {
                borderRadius: 10,
                borderColor: '#080A20',
                borderWidth: 2
              },
              label: {
                show: true,
                position: 'outside',
                formatter: '{b}',
                fontSize: 12,
                color: '#74ffbe',
                fontWeight: 'normal',
                distance: 10
              },
              labelLine: {
                show: true,
                length: 20,
                length2: 15,
                lineStyle: {
                  color: '#74ffbe',
                  width: 1,
                  type: 'solid'
                },
                smooth: 0.3
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 15,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(116, 255, 190, 0.6)',
                  borderWidth: 0
                },
                label: {
                  show: true,
                  fontSize: 14,
                  fontWeight: 'bold',
                  color: '#74ffbe'
                }
              },
              data: this.ageData
            }
          ]
        }
        
        this.chart.setOption(option)
        
        // 响应式调整
        window.addEventListener('resize', () => {
          if (this.chart) {
            this.chart.resize()
          }
        })
      })
    },
    getAgeData() {
      // 参考 leftCircle.vue 中 PieChart 组件的参数格式
      const params = {
        shengqu_name: this.sqName || '',
        diban_name: this.dbName || '',
        no_title: '',
        date_start: "2025-05-04",
        date_end: "2026-05-04"
      }
      
      this.$http.post(process.env.VUE_APP_API_HOST + "/patient_age_pie", {
        extra_params: params
      }).then((res) => {
        // 处理返回的年龄数据
        if (res.data && res.data.data) {
          this.ageData = res.data.data
          // 更新图表
          if (this.chart) {
            this.chart.setOption({
              series: [{
                data: this.ageData
              }]
            })
          }
        }
      }).catch((err) => {
        console.error('获取年龄数据失败:', err)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.age-gender-chart {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: #fff;

  .age-chart-container {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0;

    #ageChart {
      width: 100%;
      height: 100%;
      min-height: 200px;
    }
  }

  .gender-ratio {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 40px;
    padding: 15px 0;
    flex-shrink: 0;

    .gender-item {
      display: flex;
      align-items: center;
      gap: 10px;

      .gender-icon {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        font-weight: bold;

        &.male {
          background-color: rgba(72, 219, 251, 0.3);
          color: #48dbfb;
        }

        &.female {
          background-color: rgba(255, 159, 243, 0.3);
          color: #ff9ff3;
        }

        .gender-symbol {
          line-height: 1;
        }
      }

      .gender-info {
        display: flex;
        flex-direction: column;

        .gender-percent {
          font-size: 18px;
          font-weight: 600;
          color: #74ffbe;
          line-height: 1.2;
        }

        .gender-label {
          font-size: 12px;
          color: #999;
          line-height: 1.2;
          margin-top: 2px;
        }
      }
    }
  }
}
</style>

